import {
    darken,
    styled,
    SxProps,
    Theme,
    Typography,
    useTheme,
} from '@mui/material';
import { amber, green, pink, purple, red } from '@mui/material/colors';
import React, { ReactNode } from 'react';
import { yellow } from 'utils/color';
import Tooltip from '../tooltip/Tooltip';
import Symbol from 'components/Symbol';
import { IconButton } from 'components/IconButton';

const Base = styled('div', {
    shouldForwardProp: prop =>
        prop !== '$color' &&
        prop !== '$background' &&
        prop !== '$border' &&
        prop !== '$size' &&
        prop !== '$button' &&
        prop !== '$multiLine' &&
        prop !== 'sx',
})<{
    $color: string;
    $background: string;
    $border: boolean;
    $size: 'small' | 'medium' | 'large';
    $button: boolean;
    $multiLine: boolean;
}>(({ theme, $color, $background, $border, $size, $button, $multiLine }) => ({
    'display': 'flex',
    'flexDirection': 'row',
    'gap': theme.spacing(1),
    'alignItems': 'center',
    'backgroundColor': $background,
    'color': $color,
    'border': $border ? `1px solid ${$color}` : 'none',
    'padding': theme.spacing(0, $size === 'small' ? 1 : 1.25),
    'height': $multiLine
        ? undefined
        : $size === 'small'
          ? '22px'
          : $size === 'medium'
            ? '26px'
            : '32px',
    'borderRadius': '8px',
    'width': 'fit-content',
    'cursor': $button ? 'pointer' : 'default',
    'transition': 'background-color 0.5s',
    '&:hover': {
        backgroundColor:
            $button && $background !== 'transparent'
                ? darken($background, 0.1)
                : $background,
    },
    'maxWidth': '100%',
}));

const Text = styled(Typography, {
    shouldForwardProp: prop =>
        prop !== '$size' && prop !== '$multiLine' && prop !== 'sx',
})<{
    $size: 'small' | 'medium' | 'large';
    $multiLine: boolean;
}>(({ $size, $multiLine }) => ({
    overflow: 'hidden',
    textOverflow: $multiLine ? undefined : 'ellipsis',
    whiteSpace: $multiLine ? 'break-spaces !important' : 'nowrap',
    fontWeight: 500,
    color: 'inherit',
    userSelect: 'none',
    lineHeight:
        $size === 'small' ? '22px' : $size === 'medium' ? '26px' : '32px',
    height: $multiLine
        ? undefined
        : $size === 'small'
          ? '22px'
          : $size === 'medium'
            ? '26px'
            : '32px',
    textAlign: $multiLine ? 'left' : 'center',
    wordBreak: $multiLine ? 'break-word' : undefined,
}));

export type ChipColorTypes =
    | 'primary'
    | 'secondary'
    | 'green'
    | 'darkgreen'
    | 'red'
    | 'yellow'
    | 'grey'
    | 'pink'
    | 'white'
    | 'transparent'
    | 'purple'
    | 'amber';

export interface ChipProps {
    label: ReactNode;
    color: ChipColorTypes;
    symbol?: string;
    endSymbol?: string;
    onEndSymbolClick?: () => void;
    onClick?: () => void;
    border?: boolean;
    filled?: boolean;
    size?: 'small' | 'medium' | 'large';
    tooltipText?: React.ReactNode;
    sx?: SxProps<Theme>;
    textSx?: SxProps<Theme>;
    multiLine?: boolean;
}

const Chip = React.forwardRef<any, ChipProps>(
    (
        {
            label,
            color: _color,
            symbol,
            endSymbol,
            onEndSymbolClick,
            onClick,
            border = false,
            filled = false,
            size = 'medium',
            tooltipText = label,
            sx,
            textSx,
            multiLine,
        }: ChipProps,
        ref
    ) => {
        const theme = useTheme();

        const { background, color } = React.useMemo(() => {
            let background = '';
            let color = '';
            switch (_color) {
                case 'primary':
                    if (filled) {
                        background = theme.palette.primary[800];
                        color = theme.palette.primary[50];
                    } else {
                        background = theme.palette.primary[50];
                        color = theme.palette.primary[1000];
                    }
                    break;
                case 'secondary':
                    background = theme.palette.secondary[100];
                    color = theme.palette.secondary[1000];
                    break;
                case 'green':
                    if (filled) {
                        background = green[800];
                        color = green[50];
                    } else {
                        background = green[100];
                        color = green[900];
                    }
                    break;
                case 'darkgreen':
                    if (filled) {
                        background = green[900];
                        color = green[100];
                    } else {
                        background = green[900];
                        color = green[100];
                    }
                    break;
                case 'red':
                    if (filled) {
                        background = red[400];
                        color = red[50];
                    } else {
                        background = red[100];
                        color = red[900];
                    }
                    break;
                case 'yellow':
                    if (filled) {
                        background = amber[900];
                        color = 'white';
                    } else {
                        background = yellow[100];
                        color = yellow[1000];
                    }
                    break;
                case 'grey':
                    if (filled) {
                        background = theme.palette.grey[600];
                        color = theme.palette.grey[50];
                    } else {
                        background = theme.palette.grey[200];
                        color = theme.palette.grey[900];
                    }
                    break;
                case 'pink':
                    if (filled) {
                        background = pink[500];
                        color = pink[50];
                    } else {
                        background = pink[50];
                        color = pink[900];
                    }
                    break;
                case 'white':
                    background = '#ffffff';
                    color = theme.palette.text.primary;
                    break;
                case 'transparent':
                    background = 'transparent';
                    color = theme.palette.text.primary;
                    break;
                case 'purple':
                    background = purple[100];
                    color = purple[900];
                    break;
                case 'amber':
                    background = amber[100];
                    color = amber[900];
            }

            return { background, color };
        }, [_color, filled]);

        const render = () => (
            <Base
                $color={color}
                $background={background}
                $border={filled ? false : border}
                $size={size}
                $button={!!onClick}
                $multiLine={!!multiLine}
                onClick={onClick}
                sx={sx}>
                {symbol && (
                    <Symbol symbol={symbol} color={color} size='sm' filled />
                )}
                {label && (
                    <Text
                        $size={size}
                        variant={size === 'small' ? 'caption' : 'body2'}
                        sx={textSx}
                        ref={ref}
                        $multiLine={!!multiLine}>
                        {label}
                    </Text>
                )}
                {endSymbol &&
                    (onEndSymbolClick ? (
                        <IconButton
                            icon={endSymbol}
                            size='xs'
                            variant='text'
                            onClick={event => {
                                event.stopPropagation();
                                onEndSymbolClick();
                            }}
                            iconFilled
                            sx={{ color: 'inherit' }}
                        />
                    ) : (
                        <Symbol
                            symbol={endSymbol}
                            color={color}
                            size='sm'
                            filled
                        />
                    ))}
            </Base>
        );

        return tooltipText ? (
            <Tooltip title={tooltipText}>{render()}</Tooltip>
        ) : (
            render()
        );
    }
);
Chip.displayName = 'Chip';
export default Chip;
